import React from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { CheckIcon, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import { FormPlanDetails } from '@features/onboarding/components/shared/form-plan/FormPlanDetals';
import { ViewMode } from '@features/onboarding/components/shared/form-plan/MedicalPlansControls';
import { cn, isLollySkin } from '@utils/index';
import { InsurancePlan, MedicalPlan } from 'src/services/interfaces/insurance';

import { CriticalIllnessCoverageSelector } from './CriticalIllnessCoverageSelector';
import { getPlanStats as getPlanStatsUtil } from './utils';

interface ListPlanCardProps<T extends FieldValues> {
  plan: MedicalPlan;
  fullPlan?: InsurancePlan;
  form: UseFormReturn<T>;
  name: Path<T>;
  viewMode: ViewMode;
  planLength: number;
  hideRadio?: boolean;
  hidePrices?: boolean;
  isSelected: boolean;
  isCompare: boolean;
  isComparisonLimitReached: boolean;
  aiHighlightedPlanUid: string | null;
  formattedPrice: string;
  isPlanBlocked: (plan: MedicalPlan) => boolean;
  expandedPlanIds: string[];
  onToggleExpand: (planId: string) => void;
  onSelectPlan: (planId: string) => void;
  onToggleCompare: (planId: string) => void;
  onOpenCompare?: () => void;
  toggleDescription: (planId: string) => void;
  isDescriptionExpanded: (planId: string) => boolean;
  allPlans: InsurancePlan[];
  isLast?: boolean;
}

export const ListPlanCard = <T extends FieldValues>({
  plan,
  fullPlan,
  form,
  name,
  viewMode,
  planLength,
  hideRadio,
  hidePrices,
  isSelected,
  isCompare,
  isComparisonLimitReached,
  aiHighlightedPlanUid,
  formattedPrice,
  isPlanBlocked,
  expandedPlanIds,
  onToggleExpand,
  onSelectPlan,
  onToggleCompare,
  onOpenCompare,
  toggleDescription,
  isDescriptionExpanded,
  allPlans,
  isLast,
}: ListPlanCardProps<T>) => {
  const getCriticalIllnessMaxBenefit = () => {
    const sanitizeCategory = (category: string) =>
      category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const criticalIllnessPlanKey = `${sanitizeCategory('Critical Illness')}Plan`;
    const criticalIllnessCoverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
    return (
      (form.getValues?.(criticalIllnessCoverageAmountKey as any) ??
        (form.watch?.(criticalIllnessCoverageAmountKey as any) as any)) ||
      10000
    );
  };

  const planStats = getPlanStatsUtil(fullPlan, getCriticalIllnessMaxBenefit);
  // console.log('FULL CATEGORY:', fullPlan?.category);
  // console.log('Full plan details:', fullPlan);
  // console.log(
  //   'Attempting dental deductible:',
  //   getByPath(fullPlan, [
  //     'details',
  //     'coverage',
  //     'Annual Individual / Family Deductible  - In  Network',
  //   ])
  // );
  const hasLongValue = planStats.some(({ value }) => value.length > 7);
  const shouldRenderCoverageSelector =
    fullPlan?.category === 'Critical Illness' &&
    (form.watch?.(name as any) === plan.id || true);

  return (
    <article
      className={cn(
        'bg-white flex flex-col rounded-2xl overflow-hidden border border-[#ece5d8] p-3 transition-all shadow-[0px_8px_30px_rgba(15,15,15,0.05)]',
        'mt-4',
        isLast && 'mb-2',
        isSelected
          ? 'border-primary plan-selected-shadow'
          : aiHighlightedPlanUid === plan.id
            ? 'border-[rgba(113,0,54,.75)]'
            : 'border-transparent shadow'
      )}
    >
      <header
        onClick={() => !hideRadio && onSelectPlan(plan.id)}
        className={cn(
          'flex flex-col gap-6 py-1 md:py-2',
          !hideRadio && 'cursor-pointer'
        )}
      >
        <div className="flex max-lg:justify-between">
          <div className={cn(
            'flex flex-row items-start gap-3 pr-2 max-lg:max-w-full max-lg:w-full max-lg:border-r',
            hidePrices ? 'xl:max-w-[60%] max-xl:max-w-[60%]' : 'xl:max-w-[48%] max-xl:max-w-[50%]'
          )}>
            <img
              src={plan.image}
              alt={plan.title}
              className="xl:size-18 size-16 object-contain shrink-0"
            />
            <div>
              <h4
                className={cn(
                  'text-[16px] max-4xl:text-xs font-bold leading-[22px] max-4xl:leading-4 mb-1.5 text-neutral-900'
                )}
              >
                {plan.title}
              </h4>
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDescription(plan.id);
                }}
                className={cn(
                  'text-xs leading-none max-2.5xl:text-[10px] font-normal text-[#4a473f] max-w-xl cursor-pointer transition-all duration-200',
                  !isDescriptionExpanded(plan.id) && 'line-clamp-3'
                )}
                title="Click to expand"
              >
                {plan.description}
              </p>
            </div>
          </div>
          {!hidePrices && (
            <div className="flex flex-col items-center max-xl:w-[15%] justify-center text-center gap-1 md:px-4 border-l border-r md:border-[#efe8dc] max-lg:hidden">
              <span className="text-[22px] leading-7 font-bold text-black">
                {formattedPrice}
              </span>
              <span className="text-[11px] leading-3.5 font-medium text-profile-text-muted tracking-[0.02em]">
                Monthly premium
              </span>
            </div>
          )}
          <div
            className={cn(
              'flex flex-col gap-3 max-2.5xl:gap-2 px-3 md:py-1 max-lg:hidden',
              !hidePrices && 'md:border-r md:border-[#efe8dc]',
              hidePrices
                ? 'flex-1'
                : hasLongValue
                  ? 'max-xl:w-[43%] max-[1050px]:w-[54%]'
                  : 'max-xl:w-[32%]'
            )}
          >
            <dl className="flex flex-col h-fit text-[11px] tracking-[0.02em] text-profile-text-muted">
              {planStats.map(({ label, value }) => {
                const minWidthClass =
                  value.length > 7 ? 'xl:min-w-29' : 'xl:min-w-10';
                return (
                  <div
                    key={label}
                    className="flex justify-between items-center text-right gap-2"
                  >
                    <dt className="text-[11px] max-2.5xl:text-[10px] max-xl:w-1/2 font-medium text-[#b7b1a5] text-right xl:min-w-30">
                      {label}
                    </dt>
                    <dd
                      className={cn(
                        'text-[11px] w-1/2 max-xl:w-fit max-md:text-xs font-semibold text-black text-left max-xl:text-right',
                        minWidthClass
                      )}
                    >
                      {value}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
          <div className="flex flex-col w-max items-center 2.5xl:m-auto pl-4">
            {!hideRadio && (
              <div className="flex justify-end pb-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPlan(plan.id);
                  }}
                  className={cn(
                    'px-3 py-1 rounded-sm border border-primary text-xs font-semibold cursor-pointer transition-all hover:bg-primary/90',
                    isSelected
                      ? 'bg-primary text-white'
                      : 'bg-white text-primary hover:text-white'
                  )}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </button>
              </div>
            )}
            {planLength > 1 && (
              <div className={cn('mt-auto pt-4 max-2.5xl:mt-0 w-full flex flex-col gap-2', !hideRadio && 'border-t border-[#e3dbcc]')}>
                <button
                  type="button"
                  title="Compare plan"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenCompare && planLength === 2) {
                      onOpenCompare();
                      return;
                    }
                    if (isComparisonLimitReached && !isCompare) return;
                    onToggleCompare(plan.id);
                  }}
                  disabled={isComparisonLimitReached && !isCompare}
                  className={cn(
                    'flex items-center gap-3 text-[13px] font-semibold',
                    isCompare ? 'text-destructive' : 'text-[#3b3a34]',
                    isComparisonLimitReached && !isCompare
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  )}
                >
                  <div className="flex items-center gap-2 text-left text-xs max-md:text-[10px]">
                    <span
                      className={cn(
                        'size-5 max-md:size-3.5 rounded border flex items-center justify-center',
                        isCompare
                          ? 'bg-destructive border-destructive text-white'
                          : 'border-[#c9c4b6] text-transparent'
                      )}
                    >
                      <CheckIcon className="size-4" />
                    </span>
                    Compare
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {shouldRenderCoverageSelector &&
        fullPlan?.category === 'Critical Illness' && (
          <CriticalIllnessCoverageSelector
            planId={plan.id}
            plan={fullPlan as InsurancePlan}
            form={form}
            name={name}
            viewMode={viewMode}
            disabled={isPlanBlocked(plan)}
          />
        )}
      <div className="lg:hidden border-t border-b max-lg:border-b-0 border-[#e3dbcc] mt-4 pt-4 pb-3 max-md:pb-0 flex items-center flex-row justify-between max-xs:justify-start gap-3">
        {!hidePrices && (
          <div className="flex items-end justify-between gap-2 max-md:w-[20%] max-sm:w-[27%] max-xs:w-[30%] max-lg:border-r max-lg:pr-3 max-xs:pr-6">
            <div className="flex flex-col items-center text-center">
              <span className="text-xl font-bold text-black">
                {formattedPrice}
              </span>
              <span className="text-xs max-md:text-[10px] text-black">
                Monthly premium
              </span>
            </div>
          </div>
        )}
        <dl className="flex flex-col justify-between text-[11px] max-md:flex-row max-4xl:flex-wrap max-4xl:max-h-13 max-xs:pl-2 max-xs:max-h-12 max-2xs:max-h-29 flex-wrap max-md:max-h-10 w-full tracking-[0.02em] text-profile-text-muted">
          {planStats.map(({ label, value }) => (
            <div
              key={label}
              className="flex flex-row flex-wrap gap-2 items-center max-xs:justify-between"
            >
              <dt className="text-[10px] font-medium text-[#999999]">
                {label}
              </dt>
              <dd className="text-[13px] max-md:text-xs max-s:text-[10px] font-semibold text-black">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <footer className="text-black mt-4">
        <Accordion
          type="multiple"
          value={expandedPlanIds.includes(plan.id) ? ['item-1'] : []}
          onValueChange={() => onToggleExpand(plan.id)}
        >
          <AccordionItem
            value="item-1"
            className="border-t border-[#ede6da] flex flex-col"
          >
            <AccordionTrigger
              className=
              "flex justify-between items-center w-full text-[13px] pt-3 pb-0 font-semibold p-0 text-[#3b3a34] cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <span>
                {expandedPlanIds.includes(plan.id)
                  ? 'Hide Plan Details'
                  : 'Show Plan Details'}
              </span>
              {expandedPlanIds.includes(plan.id) ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </AccordionTrigger>
            <AccordionContent className="bg-white text-black pt-4">
              <FormPlanDetails
                plan={
                  allPlans.find(
                    (planItem) => planItem.uid === plan.id
                  ) as InsurancePlan
                }
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </footer>
    </article>
  );
};
